import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import { computed } from "vue";
import { addDays, endOfDay, startOfDay } from "date-fns";
import {
    DayOffFormSchema,
    type DayOffFormData,
    type DayOffDTO,
} from "@/types/DaysOff";
import { useDaysOff } from "./useDaysOff";

type UseDayOffFormOptions = {
    dayOff?: Partial<DayOffDTO>;
    onSuccess?: () => void;
};

export function useDayOffForm(options?: UseDayOffFormOptions) {
    const { createDayOff, updateDayOff } = useDaysOff();

    const isEditing = computed(() => !!options?.dayOff?.id);

    const {
        defineField,
        handleSubmit,
        errors,
        meta,
        resetForm,
        setFieldValue,
        validate,
    } = useForm<DayOffFormData>({
        validationSchema: toTypedSchema(DayOffFormSchema),
        initialValues: {
            name: options?.dayOff?.name ?? "",
            start:
                options?.dayOff?.start ??
                startOfDay(addDays(new Date(), 1)).toISOString(),
            end:
                options?.dayOff?.end ??
                endOfDay(addDays(new Date(), 2)).toISOString(),
        },
    });

    // Define fields with eager validation (validates on every change)
    const [name] = defineField("name", { validateOnModelUpdate: true });
    const [start] = defineField("start", { validateOnModelUpdate: true });
    const [end] = defineField("end", { validateOnModelUpdate: true });

    // Direct access to errors from useForm
    const fieldErrors = computed(() => ({
        name: errors.value.name,
        start: errors.value.start,
        end: errors.value.end,
    }));

    const isValid = computed(() => meta.value.valid);
    const isDirty = computed(() => meta.value.dirty);

    // Submit handler
    const onSubmit = handleSubmit((values) => {
        const payload = {
            name: values.name || null,
            start: new Date(values.start).toISOString(),
            end: values.end ? new Date(values.end).toISOString() : null,
        };

        if (isEditing.value && options?.dayOff?.id) {
            const result = updateDayOff(options.dayOff.id, payload, {
                onSuccess: options?.onSuccess,
            });

            if (!result.success) {
                console.error("Validation failed:", result.errors.flatten());
            }
        } else {
            const result = createDayOff(payload, {
                onSuccess: options?.onSuccess,
            });

            if (!result.success) {
                console.error("Validation failed:", result.errors.flatten());
            }
        }
    });

    // Helper to sync end date when start changes
    const syncEndWithStart = () => {
        const startDate = new Date(start.value);
        const endDate = new Date(end.value);

        if (endDate < startDate) {
            setFieldValue("end", start.value);
        }
    };

    return {
        // Fields (reactive, two-way bindable with v-model)
        name,
        start,
        end,

        // Validation state
        errors: fieldErrors,
        isValid,
        isDirty,

        // Actions
        onSubmit,
        resetForm,
        setFieldValue,
        syncEndWithStart,

        // State
        isEditing,
    };
}
